import React, { useMemo } from "react";
import { volumeTiles, VolTile } from "./Kocampaview";
import { volumeTotals } from "./Kocampaparse";

/* Highlights — the default landing tab.
   Tiles are live (computed from the workbook). The summary below is
   static, pre-written text: edit SUMMARY / KO_SHARE_RS10 to change it. */

const T = {
  surface: "#FFFFFF", border: "#FADDDD", accent: "#E8001A", accentDark: "#B50015",
  accentSoft: "#FFF0F0", text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", shadow: "0 2px 8px rgba(232,0,26,0.08)",
};

/* ── Editable content (KO_SHARE_RS10 also drives the 5th tile) ── */
/* ── Editable summary content ──
   Figures are Jan–Sep 2026 vs Jan–Sep 2025 from ddr_outlets.xlsx.
   KO_SHARE_RS10 and RS10_SHARE_GAIN come from CCI share data, not the
   workbook, so they are typed in by hand. */
export const KO_SHARE_RS10 = { label: "KO Share in Rs 10 Segment", metric: "CCI Share",
  year: "2026", value: "20.6%", changeLabel: "Ch vs LY", change: "0.5%", up: true };

/* Rs 10 segment share gain vs LY, last five months */
export const RS10_SHARE_GAIN = [
  ["Apr-26", "1.1%"], ["May-26", "2.3%"], ["Jun-26", "2.5%"], ["Jul-26", "2.9%"], ["Aug-26", "2.4%"],["Sep-26", "2.0%"],
];

export const SUMMARY = {
  title: "Executive Summary: Affordable Portfolio Performance",
  intro: "Affordable is now 5.43% of KO India sparkling volume for Jan–Sep 2026, up from 2.01% a year ago. Monthly share has climbed every quarter, from 4.26% in January to 6.80% in September, while affordable volume more than tripled to 59.9M UC.",
  takeaways: [
    ["📈", "Affordable contribution growth is high", null],   // filled from the live data
    ["📅", "Sep vs Aug:", "share rose from 6.11% to 6.80% (+0.69 pts) — affordable volume grew 46.6% (4.95M → 7.25M UC) against 31.7% for total KO sparkling."],
    ["🔝", "Peak month:", "June at 9.70M UC; September set the highest share of the year at 6.80%."],
  ],
  implications: [
    ["Affordable is outpacing the broader portfolio", "— it grew 208% against 13.9% for total sparkling, and its share has risen in every quarter of 2026."],
    ["Mix headroom is large and uneven", "— the best bottling units are already above 13%, while around a dozen sit under 1%. Closing that gap matters more than adding new brands."],
    ["Distribution is the lever", "— affordable mix tracks outlet coverage, and at 766K Aff CCI@10 outlets versus 1.16M Campa@10 there is still reach to win."],
  ],
  bottomLine: "The Affordable segment is compounding: A third of all incremental UC, and share still climbing into September. Continued investment in outlet coverage — especially in the low-mix business units — is the clearest route to the next step up.",
};
const H = ({ children }) => (
  <div style={{ color: T.accentDark, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
    textTransform: "uppercase", margin: "18px 0 8px" }}>{children}</div>
);

export default function Highlights({ koCampa, cci10 }) {
  const month = koCampa?.months?.[koCampa.months.length - 1] || "";
  const totals = useMemo(
    () => (koCampa ? volumeTotals(koCampa, month, "ytd") : null),
    [koCampa, month]
  );

  const volTiles = totals
    ? volumeTiles(totals).filter(t => ["koAff", "affMix", "contribution"].includes(t.id))
    : [];

  const contribTxt = totals?.contribution != null
    ? `— ${(totals.contribution * 100).toFixed(0)}% of incremental KO volume (${totals.curLabel} vs ${totals.prevLabel}) came from Affordable PET.`
    : "";

  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Tiles ── */}
      <div className="ddr-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        {cci10 && (
          <VolTile label="Aff CCI@10 (Distribution)" value={cci10.value} color={T.accent}
            icon="🏪" sub={cci10.month ? `outlets · ${cci10.month}` : undefined}
            delta={cci10.delta} deltaLabel={cci10.deltaLabel} />
        )}
        {volTiles.map(k => <VolTile key={k.id} {...k} />)}
        <VolTile label={KO_SHARE_RS10.label} value={KO_SHARE_RS10.value} color="#1A7A3A"
          icon="🎯" sub={`${KO_SHARE_RS10.metric} · ${KO_SHARE_RS10.year}`}
          deltaPts={(KO_SHARE_RS10.up ? 1 : -1) * parseFloat(KO_SHARE_RS10.change) / 100}
          deltaLabel="vs LY" />
      </div>

      {/* ── Summary ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12,
        padding: "18px 22px", boxShadow: T.shadow }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Summary</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "#5B2FA0",
            background: "#EEE8F8", border: "1px solid #D8CCF0", borderRadius: 20,
            padding: "2px 9px" }}>✨ AI Generated</span>
        </div>

        <div style={{ fontSize: 15, fontWeight: 700, color: T.accentDark, marginTop: 14 }}>{SUMMARY.title}</div>
        <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, margin: "8px 0 0" }}>{SUMMARY.intro}</p>

        <H>Key Takeaways</H>
        <ul style={{ margin: 0, paddingLeft: 4, listStyle: "none" }}>
          <li style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
            📊 <b>{KO_SHARE_RS10.label}:</b> {KO_SHARE_RS10.metric} {KO_SHARE_RS10.value} in {KO_SHARE_RS10.year}, {KO_SHARE_RS10.up ? "up" : "down"} {KO_SHARE_RS10.change} vs last year.
          </li>
          <li style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
            🚀 <b>Gaining share for the Rs 10 price segment</b> for the last {RS10_SHARE_GAIN.length} months:
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0 2px 22px" }}>
              {RS10_SHARE_GAIN.map(([m, v]) => (
                <span key={m} style={{ border: `1px solid ${T.border}`, borderRadius: 6,
                  padding: "3px 9px", fontSize: 11.5, background: "#F3FAF5" }}>
                  <span style={{ color: T.textDim }}>{m}</span>{" "}
                  <b style={{ color: T.pos }}>▲ {v}</b>
                </span>
              ))}
            </div>
          </li>
          {SUMMARY.takeaways.map(([ic, b, rest], i) => (
            <li key={i} style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
              {ic} <b>{b}</b> {rest ?? contribTxt}
            </li>
          ))}
        </ul>

        <H>Strategic Implications</H>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {SUMMARY.implications.map(([b, rest], i) => (
            <li key={i} style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
              <b>{b}</b> {rest}
            </li>
          ))}
        </ol>

        <div style={{ marginTop: 16, padding: "12px 16px", background: T.accentSoft,
          borderLeft: `4px solid ${T.accent}`, borderRadius: 6 }}>
          <b style={{ color: T.accentDark, fontSize: 13 }}>Bottom Line: </b>
          <span style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{SUMMARY.bottomLine}</span>
        </div>
      </div>
    </div>
  );
}